import React, { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslations } from '@vp/om-translations';
import { Card, GridContainer, Row, useScreenClass } from '@vp/swan';
import { ShipmentDetails } from './ShipmentDetails';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import { BillingDetails } from './BillingDetails';
import { GET_PAYMENT_DETAILS } from '../../../constants/reactQueryKeys';
import { getPaymentDetails } from '../../../apis/paymentWebApi.ts';
import useSiteData from '../../../hooks/useSiteData';
import { PaymentDetails } from './PaymentDetails';
import { getPromisedArrivalDateRange } from '../../../util/lineItemListUtils';
import { ZBD } from '../../../constants/paymentMethod';

/**
 * Parent component of billing address display, shipment details display
 * and payment details display.
 *
 * @example If the order contains physical items, show:
 * Billing address, Shipping address, shipping method and payment details
 * @example If the order only contains digital items, show:
 * Billing address and payment details
 */
export const OrderDetailsCards = () => {
  const { order, showShipmentDetails } = useContext(OrderDetailsContext);
  const { tenantEnvironment } = useSiteData();
  const [, lookUpFn] = useTranslations(['paymentMessages']);
  const { hasAllCopy } = lookUpFn('paymentMessages', [
    'paymentDetailsTitle',
    'paymentDetailsLabel',
  ]);

  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  const { data: paymentDetails } = useQuery(
    GET_PAYMENT_DETAILS,
    () =>
      getPaymentDetails({
        environment: tenantEnvironment,
        orderId: order.orderNumber,
      }),
    {
      onSuccess: payments => {
        // If payments contains ZeroBalanceDue, don't show payment details column.
        // Orders 'paid' with ZBD should not have more than one payment methods.
        setShowPaymentDetails(
          !!payments.length && payments[0].paymentMethod !== ZBD && hasAllCopy
        );
      },
      onError: () => {
        setShowPaymentDetails(false);
      },
    }
  );

  /**
   * Not likely that we don't have a billing address,
   * but this should prevent us from reading from undefined billing address
   */
  const showBillingDetails = order && order.billingAddress;

  /**
   * There should be 4 cases for the column span:
   * 1. Billing, Shipping, Ship method, Payment Details (span = 3)  Regular Item
   * 2. Billing, Payment Details (span = 6) Digital Item
   * 3. Billing, Shipping, Ship method (span = 4) Regular Item but payment details is not supported
   * 4. Billing (span = 12) Digital Item but payment details is not supported
   */
  const getColumnSpan = () => {
    let columnCount = 1; // The billing item should always be present.
    if (showShipmentDetails) columnCount += 2; // This will add 2 more cards to the row (shipping address/method)
    if (showPaymentDetails) columnCount += 1; // Add payment details if we can present this to shopper
    return 12 / columnCount;
  };

  const isMobile = useScreenClass() === 'xs';

  const columnStyle = {
    span: getColumnSpan(),
    px: isMobile ? 3 : 5,
    py: isMobile ? 4 : 0,
  };

  return (
    <Card evenHeight bordered py={isMobile ? 0 : 5} px={5}>
      <GridContainer>
        <Row>
          {showShipmentDetails && (
            <ShipmentDetails
              shipping={order.shippingAddress}
              shipmentMethod={order.orderShippingData}
              arrivalDateRange={getPromisedArrivalDateRange(order.lineItems)}
              columnStyle={columnStyle}
            />
          )}
          {showBillingDetails && (
            <BillingDetails
              billing={order.billingAddress}
              columnStyle={columnStyle}
            />
          )}
          {showPaymentDetails && (
            <PaymentDetails
              paymentDetails={paymentDetails}
              columnStyle={columnStyle}
            />
          )}
        </Row>
      </GridContainer>
    </Card>
  );
};
